import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Check, History, Inbox, Megaphone, RotateCcw, User, X } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  EmptyState,
  Heading,
  Skeleton,
  Tabs,
  useOverlayState,
} from '@heroui/react';
import { availableSpotsKeys, getAllSpotsByCondominium } from '../../../api/available-spots';
import {
  approveSpotUsage,
  cancelBulkRequest,
  cancelSpotUsage,
  finishSpotUsage,
  reclaimSpotUsage,
  getMyBulkRequests,
  getMySpotUsages,
  myParkingSpotsKeys,
  rejectSpotUsage,
  type SpotUsage,
} from '../../../api/my-parking-spots';
import { PageHeader } from '../../../components/page/page-header';
import { AsyncButton } from '../../../components/ui/async-button';
import { PageFilterButton } from '../../../components/ui/page-filter-button';
import { primaryPageTabClassName, secondaryPageTabClassName } from '../../../components/ui/page-tab-classes';
import { StatusCallout } from '../../../components/ui/status-callout';
import { TabCountBadge } from '../../../components/ui/tab-count-badge';
import { useAuth } from '../../../hooks/useAuth';
import { useCurrentCondominium } from '../../../hooks/useCurrentCondominium';
import { showErrorFromUnknown, showSuccess } from '../../../lib/toast';
import { invalidateParkingLists, parkingListQueryOptions } from '../../../lib/parking-query';
import { ConfirmUsageModal, type UsageConfirmAction } from '../../my-parking-spots/components/confirm-usage-modal';
import { BulkRequestCard } from '../components/bulk-request-card';
import { ConfirmBulkCancelModal } from '../components/confirm-bulk-cancel-modal';
import { UsageHistoryCard } from '../components/usage-history-card';
import {
  UsageHistoryFiltersDrawer,
  useUsageHistoryFiltersCount,
} from '../components/usage-history-filters-drawer';
import { UsageHistorySection } from '../components/usage-history-section';
import {
  applyUsageHistoryFilters,
  defaultUsageHistoryFilters,
  filterUsagesByCondominium,
  isOwnerUsage,
  sortUsagesByStartDesc,
  splitBulkRequests,
  splitUsagesByStatus,
  type UsageHistoryFilters,
} from '../utils/usage-history';

function invalidateUsageQueries(queryClient: ReturnType<typeof useQueryClient>) {
  return invalidateParkingLists(queryClient);
}

export default function MySpotUsagesPage() {
  const queryClient = useQueryClient();
  const { authUser, userDoc } = useAuth();
  const { condominium, label: condominiumLabel, isLoading: isCondominiumLoading } =
    useCurrentCondominium();
  const condominiumId = condominium?.id ?? '';
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<UsageHistoryFilters>(defaultUsageHistoryFilters);
  const [selectedTab, setSelectedTab] = useState(
    () => searchParams.get('tab') ?? 'my-requests',
  );
  const [requesterSubTab, setRequesterSubTab] = useState<'usage' | 'bulk'>('usage');
  const [highlightedUsageId, setHighlightedUsageId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<UsageConfirmAction | null>(null);
  const [confirmUsageId, setConfirmUsageId] = useState<string | null>(null);
  const [confirmBulkId, setConfirmBulkId] = useState<string | null>(null);

  const confirmModal = useOverlayState();
  const confirmBulkModal = useOverlayState();
  const filtersDrawer = useOverlayState();

  useEffect(() => {
    setFilters(defaultUsageHistoryFilters);
    setSelectedTab('my-requests');
    setRequesterSubTab('usage');
  }, [condominiumId]);

  useEffect(() => {
    const usageIdFromUrl = searchParams.get('usageId');
    if (!usageIdFromUrl) return;

    setHighlightedUsageId(usageIdFromUrl);
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl === 'my-spots') {
      setSelectedTab('my-spots');
    }

    const timer = window.setTimeout(() => setHighlightedUsageId(null), 5000);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('usageId');
    setSearchParams(nextParams, { replace: true });

    return () => window.clearTimeout(timer);
  }, [searchParams, setSearchParams]);

  const condoSpotsQuery = useQuery({
    queryKey: availableSpotsKeys.allSpots(condominiumId),
    queryFn: () => getAllSpotsByCondominium(condominiumId),
    enabled: !!condominiumId,
    ...parkingListQueryOptions,
  });

  const spotIdsInCondominium = useMemo(
    () => new Set((condoSpotsQuery.data ?? []).map((spot) => spot.id)),
    [condoSpotsQuery.data],
  );

  const usagesQuery = useQuery({
    queryKey: myParkingSpotsKeys.history(condominiumId),
    queryFn: getMySpotUsages,
    enabled: !!condominiumId && condoSpotsQuery.isSuccess,
    ...parkingListQueryOptions,
    select: (usages) =>
      sortUsagesByStartDesc(filterUsagesByCondominium(usages, spotIdsInCondominium)),
  });

  const bulkQuery = useQuery({
    queryKey: myParkingSpotsKeys.bulkRequests(),
    queryFn: getMyBulkRequests,
    enabled: !!condominiumId,
    ...parkingListQueryOptions,
  });

  const filteredUsages = useMemo(
    () => applyUsageHistoryFilters(usagesQuery.data ?? [], filters),
    [usagesQuery.data, filters],
  );

  const userEmail = userDoc?.email ?? authUser?.email;
  const userId = userDoc?.uid ?? authUser?.uid;

  const ownerUsages = useMemo(
    () => filteredUsages.filter((usage) => isOwnerUsage(usage, userEmail, userId)),
    [filteredUsages, userEmail, userId],
  );

  const requesterUsages = useMemo(
    () => filteredUsages.filter((usage) => !isOwnerUsage(usage, userEmail, userId)),
    [filteredUsages, userEmail, userId],
  );

  const requesterGroups = useMemo(() => splitUsagesByStatus(requesterUsages), [requesterUsages]);
  const ownerGroups = useMemo(() => splitUsagesByStatus(ownerUsages), [ownerUsages]);
  const bulkGroups = useMemo(
    () => splitBulkRequests(bulkQuery.data ?? []),
    [bulkQuery.data],
  );

  const openConfirm = (action: UsageConfirmAction, usageId: string) => {
    setConfirmAction(action);
    setConfirmUsageId(usageId);
    confirmModal.open();
  };

  const openBulkCancel = (bulkId: string) => {
    setConfirmBulkId(bulkId);
    confirmBulkModal.open();
  };

  const usageMutation = useMutation({
    mutationFn: async ({
      action,
      usageId,
    }: {
      action: UsageConfirmAction;
      usageId: string;
    }) => {
      if (action === 'cancel') await cancelSpotUsage(usageId);
      else if (action === 'reclaim') await reclaimSpotUsage(usageId);
      else await finishSpotUsage(usageId);
    },
    onSuccess: async (_, { action }) => {
      const message =
        action === 'cancel'
          ? 'Uso cancelado.'
          : action === 'reclaim'
            ? 'Vaga retomada. O morador foi notificado.'
            : 'Vaga liberada. O proprietário foi notificado.';
      showSuccess(message);
      await invalidateUsageQueries(queryClient);
    },
    onError: (error) => showErrorFromUnknown(error),
  });

  const approveMutation = useMutation({
    mutationFn: approveSpotUsage,
    onSuccess: async () => {
      showSuccess('Solicitação aprovada.');
      await invalidateUsageQueries(queryClient);
    },
    onError: (error) => showErrorFromUnknown(error),
  });

  const rejectMutation = useMutation({
    mutationFn: rejectSpotUsage,
    onSuccess: async () => {
      showSuccess('Solicitação rejeitada.');
      await invalidateUsageQueries(queryClient);
    },
    onError: (error) => showErrorFromUnknown(error),
  });

  const bulkCancelMutation = useMutation({
    mutationFn: cancelBulkRequest,
    onSuccess: async () => {
      showSuccess('Solicitação em massa cancelada.');
      await invalidateUsageQueries(queryClient);
    },
    onError: (error) => showErrorFromUnknown(error),
  });

  const handleConfirmUsage = async () => {
    if (!confirmAction || !confirmUsageId) return;
    await usageMutation.mutateAsync({ action: confirmAction, usageId: confirmUsageId });
    setConfirmAction(null);
    setConfirmUsageId(null);
  };

  const handleConfirmBulk = async () => {
    if (!confirmBulkId) return;
    await bulkCancelMutation.mutateAsync(confirmBulkId);
    setConfirmBulkId(null);
  };

  const renderRequesterCard = (usage: SpotUsage, actions: ReactNode, hint?: string) => (
    <UsageHistoryCard
      key={usage.id}
      usage={usage}
      highlighted={highlightedUsageId === usage.id}
      hint={hint}
      actions={actions}
    />
  );

  const isLoading =
    isCondominiumLoading || condoSpotsQuery.isLoading || usagesQuery.isLoading || bulkQuery.isLoading;
  const isError = usagesQuery.isError || bulkQuery.isError;

  const pendingOwnerCount = ownerGroups.pending.length;
  const requesterActionCount =
    requesterGroups.pending.length + requesterGroups.active.length;
  const activeFilterCount = useUsageHistoryFiltersCount(filters);

  const renderRequesterUsageContent = () => {
    const hasUsages =
      requesterGroups.pending.length > 0 ||
      requesterGroups.active.length > 0 ||
      requesterGroups.past.length > 0;

    if (!hasUsages) {
      return (
        <EmptyState className="flex flex-col items-center gap-2 py-12 text-center">
          <Inbox size={40} className="text-muted" />
          <Heading level={3}>Nenhum uso registrado</Heading>
          <p className="text-sm text-muted">Solicite uma vaga em Disponíveis para começar.</p>
        </EmptyState>
      );
    }

    return (
      <>
        <UsageHistorySection title="Solicitações pendentes" count={requesterGroups.pending.length}>
          {requesterGroups.pending.map((usage) =>
            renderRequesterCard(
              usage,
              <Button
                size="sm"
                variant="danger"
                className="flex-1"
                onPress={() => openConfirm('cancel', usage.id)}
              >
                <X size={14} />
                Cancelar
              </Button>,
              'Aguardando aprovação do proprietário da vaga',
            ),
          )}
        </UsageHistorySection>

        <UsageHistorySection title="Em uso" count={requesterGroups.active.length}>
          {requesterGroups.active.map((usage) =>
            renderRequesterCard(
              usage,
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  className="flex-1"
                  onPress={() => openConfirm('cancel', usage.id)}
                >
                  <X size={14} />
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  className="flex-1"
                  onPress={() => openConfirm('finish', usage.id)}
                >
                  <Check size={14} />
                  Liberar vaga
                </Button>
              </>,
            ),
          )}
        </UsageHistorySection>

        <UsageHistorySection title="Histórico" count={requesterGroups.past.length}>
          {requesterGroups.past.map((usage) => renderRequesterCard(usage, null))}
        </UsageHistorySection>
      </>
    );
  };

  const renderRequesterBulkContent = () => {
    const hasBulk = bulkGroups.active.length > 0 || bulkGroups.expired.length > 0;

    if (!hasBulk) {
      return (
        <EmptyState className="flex flex-col items-center gap-2 py-12 text-center">
          <Megaphone size={40} className="text-muted" />
          <Heading level={3}>Nenhuma solicitação em massa</Heading>
          <p className="text-sm text-muted">
            Use a opção de solicitação em massa em Disponíveis quando precisar de uma vaga com
            urgência.
          </p>
        </EmptyState>
      );
    }

    return (
      <>
        <UsageHistorySection title="Ativas" count={bulkGroups.active.length}>
          {bulkGroups.active.map((request) => (
            <BulkRequestCard
              key={request.id}
              request={request}
              onCancel={() => openBulkCancel(request.id)}
            />
          ))}
        </UsageHistorySection>

        <UsageHistorySection title="Expiradas" count={bulkGroups.expired.length}>
          {bulkGroups.expired.map((request) => (
            <BulkRequestCard key={request.id} request={request} />
          ))}
        </UsageHistorySection>
      </>
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Histórico"
        description={
          condominiumLabel
            ? `Empréstimos, aluguéis e solicitações em ${condominiumLabel}.`
            : 'Empréstimos, aluguéis e solicitações.'
        }
        actions={
          !isCondominiumLoading && condominiumId ? (
            <PageFilterButton
              activeFilterCount={activeFilterCount}
              onPress={filtersDrawer.open}
            />
          ) : null
        }
      />

      {isLoading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-12 w-full max-w-md rounded-xl" />
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-40 w-full rounded-xl" />
            ))}
          </div>
        </div>
      ) : isError ? (
        <StatusCallout
          status="danger"
          title="Erro ao carregar histórico"
          action={
            <Button
              size="sm"
              variant="secondary"
              onPress={() => {
                void usagesQuery.refetch();
                void bulkQuery.refetch();
              }}
            >
              Tentar novamente
            </Button>
          }
        >
          Não foi possível carregar seus usos. Tente novamente em instantes.
        </StatusCallout>
      ) : (
        <Tabs
          selectedKey={selectedTab}
          onSelectionChange={(key) => {
            const nextTab = String(key);
            setSelectedTab(nextTab);
            const nextParams = new URLSearchParams(searchParams);
            if (nextTab === 'my-requests') nextParams.delete('tab');
            else nextParams.set('tab', nextTab);
            setSearchParams(nextParams, { replace: true });
          }}
        >
          <Tabs.ListContainer>
            <Tabs.List aria-label="Histórico de uso" className="p-1.5">
              <Tabs.Tab id="my-requests" className={primaryPageTabClassName}>
                <span className="inline-flex items-center gap-1.5 sm:gap-2">
                  <User size={16} className="shrink-0 sm:h-[18px] sm:w-[18px]" />
                  Usadas por mim
                </span>
                <Tabs.Indicator />
              </Tabs.Tab>
              <Tabs.Tab id="my-spots" className={primaryPageTabClassName}>
                <span className="inline-flex items-center gap-1.5 sm:gap-2">
                  Minhas vagas
                  <TabCountBadge count={pendingOwnerCount} color="warning" />
                </span>
                <Tabs.Indicator />
              </Tabs.Tab>
            </Tabs.List>
          </Tabs.ListContainer>

          <Tabs.Panel id="my-requests" className="flex flex-col gap-4 pt-3">
            <Tabs
              variant="secondary"
              selectedKey={requesterSubTab}
              onSelectionChange={(key) => setRequesterSubTab(key === 'bulk' ? 'bulk' : 'usage')}
            >
              <Tabs.ListContainer>
                <Tabs.List aria-label="Usadas por mim" className="w-full">
                  <Tabs.Tab id="usage" className={secondaryPageTabClassName}>
                    <span className="inline-flex items-center gap-1 sm:gap-1.5">
                      <History size={14} className="shrink-0 sm:size-[15px]" />
                      <span className="sm:hidden">Uso</span>
                      <span className="hidden sm:inline">Histórico de uso</span>
                      <TabCountBadge count={requesterActionCount} color="warning" />
                    </span>
                    <Tabs.Indicator />
                  </Tabs.Tab>
                  <Tabs.Tab
                    id="bulk"
                    className={secondaryPageTabClassName}
                    aria-label="Solicitação em massa"
                  >
                    <span className="inline-flex items-center gap-1 sm:gap-1.5">
                      <Megaphone size={14} className="shrink-0 sm:size-[15px]" />
                      <span className="sm:hidden">Em massa</span>
                      <span className="hidden sm:inline">Solicitação em massa</span>
                      <TabCountBadge count={bulkGroups.active.length} color="accent" />
                    </span>
                    <Tabs.Indicator />
                  </Tabs.Tab>
                </Tabs.List>
              </Tabs.ListContainer>

              <Tabs.Panel id="usage" className="flex flex-col gap-8 pt-2">
                {renderRequesterUsageContent()}
              </Tabs.Panel>

              <Tabs.Panel id="bulk" className="flex flex-col gap-8 pt-2">
                {renderRequesterBulkContent()}
              </Tabs.Panel>
            </Tabs>
          </Tabs.Panel>

          <Tabs.Panel id="my-spots" className="flex flex-col gap-8 pt-4">
            <UsageHistorySection title="Solicitações pendentes" count={ownerGroups.pending.length}>
              {ownerGroups.pending.map((usage) => (
                <UsageHistoryCard
                  key={usage.id}
                  usage={usage}
                  highlighted={highlightedUsageId === usage.id}
                  showRequester
                  hint="Aguardando sua aprovação"
                  actions={
                    <>
                      <AsyncButton
                        size="sm"
                        variant="primary"
                        className="flex-1"
                        isPending={
                          approveMutation.isPending && approveMutation.variables === usage.id
                        }
                        isDisabled={
                          rejectMutation.isPending ||
                          (approveMutation.isPending && approveMutation.variables !== usage.id)
                        }
                        pendingLabel="Aprovar"
                        onPress={() => approveMutation.mutate(usage.id)}
                      >
                        <Check size={14} />
                        Aprovar
                      </AsyncButton>
                      <AsyncButton
                        size="sm"
                        variant="danger"
                        className="flex-1"
                        isPending={
                          rejectMutation.isPending && rejectMutation.variables === usage.id
                        }
                        isDisabled={
                          approveMutation.isPending ||
                          (rejectMutation.isPending && rejectMutation.variables !== usage.id)
                        }
                        pendingLabel="Rejeitar"
                        onPress={() => rejectMutation.mutate(usage.id)}
                      >
                        <X size={14} />
                        Rejeitar
                      </AsyncButton>
                    </>
                  }
                />
              ))}
            </UsageHistorySection>

            <UsageHistorySection title="Em uso" count={ownerGroups.active.length}>
              {ownerGroups.active.map((usage) => (
                <UsageHistoryCard
                  key={usage.id}
                  usage={usage}
                  showRequester
                  actions={
                    <Button
                      size="sm"
                      variant="danger"
                      className="flex-1"
                      onPress={() => openConfirm('reclaim', usage.id)}
                    >
                      <RotateCcw size={14} />
                      Retomar vaga
                    </Button>
                  }
                />
              ))}
            </UsageHistorySection>

            <UsageHistorySection title="Histórico" count={ownerGroups.past.length}>
              {ownerGroups.past.map((usage) => (
                <UsageHistoryCard key={usage.id} usage={usage} showRequester />
              ))}
            </UsageHistorySection>

            {ownerGroups.pending.length === 0 &&
            ownerGroups.active.length === 0 &&
            ownerGroups.past.length === 0 ? (
              <EmptyState className="flex flex-col items-center gap-2 py-12 text-center">
                <History size={40} className="text-muted" />
                <Heading level={3}>Nenhum uso nas suas vagas</Heading>
                <p className="text-sm text-muted">
                  Quando alguém solicitar uma das suas vagas, aparecerá aqui.
                </p>
              </EmptyState>
            ) : null}
          </Tabs.Panel>
        </Tabs>
      )}

      <ConfirmUsageModal
        state={confirmModal}
        action={confirmAction}
        onConfirm={handleConfirmUsage}
      />

      <ConfirmBulkCancelModal state={confirmBulkModal} onConfirm={handleConfirmBulk} />

      {filtersDrawer.isOpen ? (
        <UsageHistoryFiltersDrawer
          state={filtersDrawer}
          appliedFilters={filters}
          onApply={setFilters}
        />
      ) : null}
    </div>
  );
}
